'use strict';

/**
 *Common functionality for both server and client.
 *
 * We declare a module for both server and client using the
 * technique from: http://caolanmcmahon.com/posts/writing_for_node_and_the_browser/.
 */

{
  let implementation = function(exports, _) {
    /**
     * Time unit multipliers for getting millisecond time.
     */
    let timeUnitMultipliers = {
      /**
       * Milliseconds multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      milliseconds: function() {
        return 1;
      },

      /**
       * Seconds multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      seconds: function() {
        return 1000;
      },

      /**
       * Minutes multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      minutes: function() {
        return this.seconds() * 60;
      },

      /**
       * Hours multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      hours: function() {
        return this.minutes() * 60;
      },

      /**
       * Days multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      days: function() {
        return this.hours() * 24;
      },

      /**
       * Weeks multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      weeks: function() {
        return this.days() * 7;
      },

      /**
       * Months multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      months: function() {
        return this.days() * 31;
      },

      /**
       * Quarters multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      quarters: function() {
        return this.days() * 91;
      },

      /**
       * Years multiplier.
       *
       * @returns {Number} Multiplier for milliseconds.
       */
      years: function() {
        return this.days() * 365;
      }
    };

    let defaultDateTimeFormat = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };

    /**
     * Date-time pre-compiled format map.
     */
    let dateTimeFormats = typeof Intl !== 'undefined' &&
      { 'ru-RU': new Intl.DateTimeFormat('ru-RU', defaultDateTimeFormat) };

    /**
     * Saymon class ID definitions.
     */
    exports.classIds = {
      Root: 1,
      SaymonAgent: 2,
      Host: 3,
      Cpu: 4,
      Memory: 5,
      FileSystem: 6,
      Process: 7,
      Node: 13,
      Hardware: 20,
      Info: 24,
      Cloud: 29,
      ObjectGroup: 30,
      NetworkIo: 31,
      User: 32,
      NetworkLink: 35,
      ConfigurationFile: 42,
      Dashboard: 43,
      ZabbixHost: 44
    };

    /**
     * Saymon state IDs.
     */
    exports.stateIds = {
      Created: 1,
      Working: 3,
      Overloaded: 4,
      Alarm: 5,
      Stopped: 6,
      NoData: 7,
      Down: 8,
      Disabled: 9
    };

    /**
     * Incident state IDs.
     */
    exports.incidentLevelIds = {
      Alarm: 1,
      Warning: 2,
      Cleared: 3,
      Major: 4
    };

    /**
     * Saymon property type ID definitions.
     */
    exports.propTypeIds = {
      Info: 1,
      Readonly: 2,
      ReadWrite: 3,
      ExternalDocument: 6,
      UploadedDocument: 7,
      Service: 8
    };

    /**
     * Entity operation types.
     */
    exports.operationTypes = {
      Mqtt: 1,
      Exec: 2
    };

    /**
     * Property owner types.
     */
    exports.numericEntityTypes = {
      Object: 1,
      Link: 2,
      Flow: 3,
      Class: 4,
      User: 5,
      StateConditions: 6,
      StateTriggers: 7,
      StateHistory: 8,
      Reference: 9,
      Property: 10,
      HistoryAnnotation: 11,
      UserGroup: 12,
      Session: 13,
      NotificationTemplate: 14,
      Operation: 15,
      IncidentConditions: 16,
      State: 17,
      Dictionary: 18,
      Configuration: 19,
      StatRules: 20,
      IncidentLevel: 21
    };

    /**
     * State change reason codes.
     */
    exports.stateChangeReasonCodes = {
      ChildGreaterPriority: 1,
      ChildLessPriority: 2,
      MissedAllBranches: 3,
      AgentResultError: 4,
      NoChildrenLeft: 5,
      AgentResultTimeout: 6,
      AgentDown: 7,
      MonitoringDisabled: 8,
      AgentTaskRemoved: 9,
      ChildObject: 10,
      NoStatPresent: 11,
      ExternalChange: 12,
      ManualChange: 13,
      PeriodBranchOnly: 14
    };

    /**
     * Identifiers of special actions available on state change condition branches.
     * Pull of available ids for this group is 70-99.
     */
    exports.stateChangeConditionActionsIds = {
      Ignore: 70
    };

    /**
     * Saymon user error status codes.
     */
    exports.saymonStatusCodes = {
      DocumentConversionPending: 1,
      BadFileType: 2,
      BadLogin: 3,
      BadCurrentPassword: 4,
      ProcessingTimeout: 5,
      ActionDenied: 7,
      UserAlreadyExists: 8,
      PropertyAlreadyExists: 9,
      TemplateSyntaxError: 10,
      LicenseViolationError: 11,
      LinkedObjectRemoval: 12,
      BadCaptcha: 13,
      BadEmail: 14,
      BadToken: 15,
      ActivationError: 16,
      RestoreError: 17,
      MailSenderError: 18,
      EmailDoesNotExists: 19,
      EmailAlreadyUsed: 20,
      ServiceUnavailable: 22,
      OperationIsUsedInTriggersError: 23,
      OperationIsUsedInTriggersOfDependentObjectsError: 24
    };

    /**
     * Event log levels.
     */
    exports.eventLevels = {
      Clear: 0,
      Minor: 1,
      Major: 2,
      Critical: 3
    };

    /**
     * Entity change types.
     */
    exports.changeType = {
      Created: 1,
      Modified: 2,
      Removed: 3
    };

    /**
     * User source types.
     */
    exports.userSourceType = {
      Native: 0,
      Ldap: 1
    };

    /**
     * User status.
     */
    exports.userStatus = {
      NeedActivation: 0,
      NeedActivationWithPassword: 1,
      Active: 2,
      Blocked: 3
    };

    exports.notificationTemplateGroups = {
      State: 'state',
      Auth: 'auth'
    };

    /**
     * Event Log record type.
     */
    exports.eventLogRecordType = {
      Snmp: 1,
      Mqtt: 2
    };

    /**
     * Data schemas.
     */
    exports.schemas = {
      Tag: {
        type: 'object',
        properties: {
          id: {
            type: 'string'
          },
          name: {
            type: 'string'
          }
        }
      },
      State: {
        type: 'object',
        properties: {
          id: {
            type: ['string', 'number']
          },
          name: {
            type: 'string'
          },
          priority: {
            type: 'number'
          },
          ui: {
            type: 'object',
            properties: {
              colors: {
                type: 'object',
                properties: {
                  main: {
                    type: 'string'
                  },
                  shadow: {
                    type: 'string'
                  },
                  light: {
                    type: 'string'
                  },
                  background: {
                    type: 'string'
                  }
                }
              }
            }
          }
        }
      },
      IncidentLevel: {
        type: 'object',
        properties: {
          id: {
            type: ['string', 'number']
          },
          name: {
            type: 'string'
          },
          ui: {
            type: 'object',
            properties: {
              colors: {
                type: 'object',
                properties: {
                  main: {
                    type: 'string'
                  }
                }
              }
            }
          }
        }
      },
      StateConditions: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            condition: {
              type: 'object'
            },
            state: {
              type: ['number', 'string']
            },
            description: {
              type: 'string'
            }
          },
          required: ['condition', 'state']
        }
      },
      IncidentConditions: {
        type: 'array',
        definitions: {
          condition: {
            type: 'object'
          }
        },
        items: {
          type: 'object',
          properties: {
            condition: {
              $ref: '#/definitions/condition'
            },
            clearCondition: {
              $ref: '#/definitions/condition'
            },
            reference: {
              type: 'array',
              items: {
                type: 'number'
              }
            },
            state: {
              type: ['number', 'string']
            },
            text: {
              type: 'string'
            },
            id: {
              type: 'string'
            }
          },
          allOf: [
            { required: ['state'] },
            {
              oneOf: [
                { required: ['condition'] },
                { required: ['reference'] }
              ]
            }
          ]
        },
        IncidentFilter: {
          type: 'array',
          items: {
            type: 'array'
          }
        }
      },
      Script: {
        type: 'object',
        properties: {
          id: {
            type: 'string'
          },
          name: {
            type: 'string'
          },
          text: {
            type: 'string'
          }
        }
      },
      Operation: {
        type: 'object',
        properties: {
          id: {
            type: 'string'
          },
          name: {
            type: 'string'
          },
          type: {
            type: 'number'
          },
          parameters: {
            type: 'object'
          }
        }
      },
      Property: {
        type: 'object',
        properties: {
          id: {
            type: ['string', 'number']
          },
          name: {
            type: 'string'
          },
          type_id: {
            type: 'number'
          },
          value: {
            type: 'string'
          }
        }
      },
      StatRules: {
        type: 'array',
        items: {
          type: 'object',
          properties: {
            conditions: {
              type: 'array',
              items: {
                type: 'object'
              }
            },
            actions: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  type: {
                    type: 'string',
                    enum: ['set', 'extend', 'drop']
                  }
                },
                required: ['type'],
                additionalProperties: true
              }
            }
          },
          required: ['actions']
        }
      }
    };

    /**
     * Default states.
     */
    exports.defaultStates = _.indexBy([
      {
        id: exports.stateIds.Created,
        name: 'Created',
        priority: 2,
        ui: {
          colors: {
            main: '#ce86f7',
            shadow: 'rgba(206, 134, 247, 0.5)',
            light: 'rgba(206, 134, 247, 0.1)',
            background: ''
          }
        }
      },
      {
        id: exports.stateIds.Working,
        name: 'Working',
        priority: 4,
        ui: {
          colors: {
            main: '#04930c',
            shadow: 'rgba(4, 147, 12, 0)',
            light: 'rgba(4, 147, 12, 0.1)',
            background: 'rgba(4, 147, 12, 0.1)'
          }
        }
      },
      {
        id: exports.stateIds.Overloaded,
        name: 'Overloaded',
        priority: 6,
        ui: {
          colors: {
            main: '#fd7607',
            shadow: 'rgba(253, 118, 7, 0)',
            light: 'rgba(253, 118, 7, 0.6)',
            background: 'rgba(253, 118, 7, 0.6)'
          }
        }
      },
      {
        id: exports.stateIds.Alarm,
        name: 'Alarm',
        priority: 7,
        ui: {
          colors: {
            main: '#ff0000',
            shadow: 'rgba(255, 0, 0, 0)',
            light: 'rgba(255, 0, 0, 0.4)',
            background: 'rgba(194, 31, 31, 0.4)'
          }
        }
      },
      {
        id: exports.stateIds.Stopped,
        name: 'Stopped',
        priority: 1,
        ui: {
          colors: {
            main: '#000000',
            shadow: 'rgba(255, 0, 0, 0.5)',
            light: 'rgba(255, 0, 0, 0.1)',
            background: ''
          }
        }
      },
      {
        id: exports.stateIds.NoData,
        name: 'No data',
        priority: 5,
        ui: {
          colors: {
            main: '#fabe28',
            shadow: 'rgba(250, 190, 40, 0)',
            light: 'rgba(250, 190, 40, 0.1)',
            background: 'rgba(250, 190, 40, 0.1)'
          }
        }
      },
      {
        id: exports.stateIds.Down,
        name: 'Down',
        priority: 3,
        ui: {
          colors: {
            main: '#810000',
            shadow: 'rgba(129, 0, 0, 0.56)',
            light: 'rgba(129, 0, 0, 0.1)',
            background: ''
          }
        }
      },
      {
        id: exports.stateIds.Disabled,
        name: 'Disabled',
        priority: 0,
        ui: {
          colors: {
            main: '#646166',
            shadow: 'rgba(190, 190, 190, 0.5)',
            light: 'rgba(190, 190, 190, 0.1)',
            background: ''
          }
        }
      }
    ], 'id');

    /**
     * Default incident levels.
     */
    exports.defaultIncidentLevels = _.indexBy([
      {
        id: exports.incidentLevelIds.Alarm,
        name: 'Alarm',
        ui: {
          colors: {
            main: '#ff0000'
          }
        }
      },
      {
        id: exports.incidentLevelIds.Warning,
        name: 'Warning',
        ui: {
          colors: {
            main: '#fd7607'
          }
        }
      },
      {
        id: exports.incidentLevelIds.Cleared,
        name: 'Cleared',
        ui: {
          colors: {
            main: '#04930c'
          }
        }
      },
      {
        id: exports.incidentLevelIds.Major,
        name: 'Major',
        ui: {
          colors: {
            main: '#ff0000'
          }
        }
      }
    ], 'id');

    /**
     * Gets a property of an object by dot-separated path.
     *
     * @param {Object|String} object Input object or JSON string.
     * @param {String} path Property path.
     * @param {Object} [options] Function options.
     * - {Boolean} parseJson If true, the function will attempt to parse nested JSON.
     * (i.e. if name is 'a.b.c', the function will first look for a field with name 'a.b.c').
     * @returns {*} Property value or undefined, if the property was not found.
     */
    exports.getPropertyByPath = function(object, path, options) {
      options = options || {};

      if (!object || !path) return;

      let obj = object;
      let path0 = path.split('.');

      while (obj && path0.length > 0) {
        if (options.parseJson && typeof obj === 'string') {
          try {
            obj = JSON.parse(obj);
          }
          catch (e) {
          }
        }

        let name0 = path0.shift();
        let key = [name0];
        let actualValue = obj[name0];

        while (actualValue === undefined && path0.length > 0) {
          key.push(path0.shift());

          name0 = key.join('.');
          actualValue = obj[name0];
        }

        obj = actualValue;
      }

      return obj;
    };

    /**
     * Sets a property of an object by dot-separated path. Modifies input object.
     *
     * @param {Object} object Object to set property in.
     * @param {String} path Field path.
     * @param {*} value Field value.
     * @returns {Object} Input object.
     */
    exports.setPropertyByPath = function(object, path, value) {
      if (!object || !path) return object;

      let obj = object;
      let path0 = path.split('.');

      while (obj && path0.length > 0) {
        let name0 = path0.shift();
        let final = path0.length === 0;

        if (final) {
          obj[name0] = value;
        }
        else if (exports.isPlainObject(obj[name0])) {
          obj = obj[name0];
        }
        else if (!exports.isDefined(obj[name0])) {
          obj[name0] = {};
          obj = obj[name0];
        }
        else {
          obj = null;
        }
      }

      return object;
    };

    /**
     * Deletes a property from an object by dot-separated path. Modifies input object.
     *
     * @param {Object} object Object to delete property from.
     * @param {String} path Field path.
     * @param {Object} [options] Operation options.
     * - {Boolean} parseJson Whether to parse nested stringified JSON.
     * @returns {Object} Input object.
     */
    exports.deletePropertyByPath = function(object, path, options = {}) {
      if (!object || !path) return object;

      let obj = object;
      let path0 = path.split('.');
      let parseJson = options.parseJson;

      while (obj && path0.length > 0) {
        let name0 = path0.shift();
        let final = path0.length === 0;
        let val = obj[name0];

        if (parseJson && typeof val === 'string') {
          try {
            val = JSON.parse(val);
            obj[name0] = val;
          }
          catch (e) {
          }
        }

        if (final) {
          delete obj[name0];
        }
        else if (exports.isPlainObject(val)) {
          obj = val;
        }
        else if (!exports.isDefined(val)) {
          return object;
        }
        else {
          obj = null;
        }
      }

      return object;
    };

    /**
     * Gets all properties of an object by dot-separated wildcard.
     *
     * @param {Object} object Input object.
     * @param {String} path Property path.
     * @returns {Array} Matching properties array.
     */
    exports.getPropertiesByWildcard = function(object, path) {
      if (!object || !path) return [];

      let ret = [object];
      let path0 = path.split('.');

      while (ret.length > 0 && path0.length > 0) {
        const key0 = path0.shift();

        if (key0.indexOf('*') > -1) {
          // Wildcard comparison.
          const keyRx = new RegExp(key0.replace('*', '.*'));

          ret = _.reduce(ret, (memo, value) => {
            if (_.isObject(value)) {
              _.each(value, (value1, key1) => {
                if (key1.match(keyRx)) {
                  memo.push(value1);
                }
              });
            }

            return memo;
          }, []);
        }
        else {
          // Simple comparison.
          ret = exports.transform(ret, value => _.isObject(value) && value[key0]);
        }
      }

      return ret;
    };

    /**
     * Converts a number to human-readable form, with order-of-magnitude
     * postfix.
     *
     * Examples: 1000 => 1.0 K; 1500 => 1.5 K; 1000000 => 1.0 M; 1500000 => 1.5 M.
     *
     * @param {Number|String} n Number to convert.
     * @param {Object} [options] Function options.
     * - {Boolean} bytes Bytes mode flag. In bytes mode, 1 K = 1024 instead of 1000. False by default.
     * - {Number} precision Floating point precision for the resulting number, i.e. how many digits after decimal point.
     * - {String} separator Separator label. Default ' '.
     * - {Boolean} pair Return result as array of number and unit.
     * numbers will be present in the resulting floating point number. 3 by default.
     * @returns {String|Array} Human-readable string representation of a number, or input, if it is not supported.
     */
    exports.humanReadableNumber = function(n, options) {
      if (!exports.isNumber(n)) return n;

      let n0 = Math.abs(n);
      let sign = n > 0 ? 1 : -1; // Actual sign of the number.
      options = options || {};
      options.separator = typeof options.separator === 'string' ? options.separator : ' ';

      if (typeof n0 == 'string') {
        n0 = parseFloat(n);
      }

      let units = 'KMGTPEZYXWVU';
      let thresh = options.bytes ? 1024 : 1000;

      // Not Number or NaN.
      if (typeof n0 != 'number' || !isFinite(n0) || n0 == 0) {
        return options.pair ? [n, ''] : n;
      }
      else if (n0 < thresh) {
        let numPart = Math.trunc(n0);
        let rationalPart = parseFloat((n0 - numPart).toPrecision(options.precision || 3));
        let roundedResult = (numPart + rationalPart) * sign;

        return options.pair ? [roundedResult, ''] : roundedResult;
      }

      let u = -1;

      do {
        n0 /= thresh;

        u += 1;
      }
      while (n0 >= thresh);

      let resultNumber = parseFloat(n0.toPrecision(options.precision || 3)) * sign;

      return options.pair ? [resultNumber, units.charAt(u)] : resultNumber + options.separator + units.charAt(u);
    };

    /**
     * Parses a number from a human-readable number string.
     *
     * Examples: 1.0 K => 1000; 1.5 K => 1500; 124 => 124; 1.5 M => 1500000.
     *
     * @param {String|Number} s Input string or number. If a number is provided, it is
     * immediately returned.
     * @param {Object} [options] Operation options.
     * - {Boolean} bytes Bytes mode flag. In bytes mode, 1 K = 1024 instead of 1000. False by default.
     * @returns {Number} Parsed number or NaN if parsing failed.
     */
    exports.fromHumanReadableNumber = function(s, options) {
      if (typeof s == 'number') return s;

      let matchResult = s.match(/(\d+\.?\d*)\s*([KMGTPEZYXWVU]?)/);

      if (!matchResult) return NaN;

      let number = parseFloat(matchResult[1]);
      let multiplierBase = options && options.bytes === true && 1024 || 1000;
      let multiplierPower = matchResult[2] && 'KMGTPEZYXWVU'.indexOf(matchResult[2]) + 1 || 0;
      let multiplier = Math.pow(multiplierBase, multiplierPower);

      return number * multiplier;
    };

    /**
     * Transforms an input array or object to a new array, possibly filtering
     * out some elements and changing existing elements.
     *
     * @param {Array|Object} iterable Iterable (array or object).
     * @param {Function} iter Iterator function. Receives a current value and key as input parameters.
     * Returns either a new value, that is pushed to output array, or undefined or false,
     * meaning that the element should be skipped.
     * @returns {Array} Resulting array.
     */
    exports.transform = function(iterable, iter) {
      let ret = [];
      let iter0 = (value, key) => {
        let res = iter(value, key);

        (res || res === 0) && ret.push(res);
      };

      if (_.isArray(iterable)) {
        iterable.forEach(iter0);
      }
      else {
        _.each(iterable, iter0);
      }

      return ret;
    };

    /**
     * Transforms an object into another object. Runs a transformation function over the
     * input object key-value pairs and either modifies a value of a key-value pair, removes
     * the key-value pair, or substitutes it with the provided one.
     *
     * @param {Object} obj Input object.
     * @param {Function} iter Iterator (transformation) function. Iterates through object key-value pairs.
     * Receives a value as a first argument and a key as a second argument. Possible return values:
     * - true: Leave key-value pair unchanged (will be copied verbatim to resulting object).
     * - Array[2]: Use new key-value pair for original one.
     * - false|undefined: Skip this key-value pair (won't be present in resulting object).
     * - any other value: Use original key with a new value.
     * @param {Object} [context] Iterator function context (this object).
     * @returns {Object} Resulting object.
     */
    exports.transformObject = function(obj, iter, context) {
      let ret = {};

      Object.keys(obj).forEach(function(key, idx) {
        let res = iter.call(this, obj[key], key, idx);

        if (res === true) { // Leave key-value pair unchanged.
          ret[key] = obj[key];
        }
        else if (res instanceof Array && res.length == 2) { // Convert to another key-value pair.
          ret[res[0]] = res[1];
        }
        else if (res !== false && res !== undefined) { // Use new value with original key.
          ret[key] = res;
        }
      }, context || this);

      return ret;
    };

    /**
     * Similar to transformObject(), but runs the transformation recursively on
     * object and array values.
     *
     * @param {Object} obj Input object.
     * @param {Function} iter Iterator (transformation) function. See transformObject() for details.
     * @param {Object} [context]  Iterator function context (this object).
     * @param {Object} [options] Operation options.
     * - {Boolean} arrays If false - won't recurse into arrays.
     * @returns {Object} Resulting object.
     */
    exports.transformObjectRecursive = function(obj, iter, context, options) {
      options = options || {};

      return exports.transformObject(obj, function(value, key, idx) {
        if (exports.isPlainObject(value)) {
          return exports.transformObjectRecursive(value, iter, context, options);
        }

        if (Array.isArray(value) && options.arrays !== false) {
          let value0 = exports.transform(value, function(value, key) {
            if (exports.isPlainObject(value)) {
              return exports.transformObjectRecursive(value, iter, context, options);
            }

            let value1 = iter.call(context, value, key, key);

            if (value1 === true) return value;

            return value1;
          });

          return [key, value0];
        }

        return iter.call(context, value, key, idx);
      }, context);
    };

    /**
     * Recursive reduce for objects.
     *
     * @param {Object|undefined} obj Object to reduce.
     * @param {Function} iter Iteratee function.
     * @param {Object|Array| Undefined} memo Initial state of the reduction.
     * @param {Object} [options] Options.
     * - {Number} [depth] Recursion depth.
     * - {Function} [keyFunction] Function for key generation.
     * - {Function} [filter] Function for key filtering.
     * - {Boolean} [parseJson] Whether to parse nested JSON.
     * - {Boolean} [skipObjects] Do not call value of an object. False by default.
     * - {String[]} [keepFields] Names of fields, that should not be flattened.
     * @returns {Object|Array} Flattened object or undefined if input was undefined.
     */
    exports.reduceRecursive = function(obj, iter, memo, options) {
      options = options || {};
      let parseJson = options.parseJson;
      let depth = options.depth;
      let filter = options.filter;
      let keepFields = options.keepFields;
      let skipObjects = options.skipObjects;
      let keyFunc = options.keyFunction;
      memo = _.isUndefined(memo) ? {} : memo;

      if (depth <= 0) return memo;

      let recurse = function(obj0, memo0, path, depth) {
        return _.reduce(obj0, (memo1, val, key) => {
          if (_.isFunction(val) || val === undefined || val === null) return memo1;

          if (parseJson && typeof val === 'string') {
            try {
              val = JSON.parse(val);
            }
            catch (e) {
            }
          }

          let key0 = keyFunc && keyFunc(key + '', obj0) || key;
          let fullPath = path + key0;
          let valIsObject = _.isObject(val);

          if ((!(valIsObject && skipObjects) || depth <= 0)) {
            if (!(filter && (filter(key, obj0, val) === false))) {
              memo1 = iter(memo1, val, key, fullPath, depth);
            }
          }

          fullPath += '.';

          if (_.contains(keepFields, key)) {
            memo1 = recurse(val, memo1, fullPath, 0);
          }
          else if (depth > 0) {
            memo1 = recurse(val, memo1, fullPath, depth - 1);
          }
          else if (valIsObject && _.isUndefined(depth)) {
            memo1 = recurse(val, memo1, fullPath);
          }

          return memo1;
        }, memo0);
      };

      return recurse(obj, memo, '', depth > 0 ? depth - 1 : undefined);
    };

    /**
     * Flattens an object, converting any nested objects
     * into fields.
     *
     * @param {Object|undefined} obj Object to flatten.
     * @param {Object} [options] Flattening options.
     * - {Number} [depth] Recursion depth.
     * - {Function} [keyFunction] Function for key generation.
     * - {Function} [filter] Function for key filtering.
     * - {Boolean} [parseJson] Whether to parse nested JSON.
     * - {String[]} [keepFields] Names of fields, that should not be flattened.
     * @returns {Object|undefined} Flattened object or undefined if input was undefined.
     */
    exports.flatten = function(obj, options) {
      if (!obj) return;

      return this.reduceRecursive(obj, (memo, value, key, path) => {
        memo[path] = value;

        return memo;
      }, {}, _.defaults({ skipObjects: true }, options));
    };

    /**
     * Converts a flat object with dot-separated path keys to hierarchical object.
     * This operation is opposite to flatten().
     *
     * @param {Object} obj Object to convert.
     * @returns {Object} Conversion result.
     */
    exports.unflatten = function(obj) {
      return _.reduce(obj, (memo, value, key) => {
        exports.setPropertyByPath(memo, key, value);

        return memo;
      }, {});
    };

    /**
     * Adds an element to a multilist under given index.
     *
     * @param {Object} cont A container for multilist.
     * @param {*} idx Index, under which to put an element.
     * @param {*} elem Element.
     */
    exports.pushMultilist = function(cont, idx, elem) {
      if (!cont[idx]) {
        cont[idx] = [elem];
      }
      else {
        cont[idx].push(elem);
      }
    };

    /**
     * Generates a key-value pair object.
     *
     * @param {*} key Key.
     * @param {*} value Value.
     * @returns {Object} Key-value pair object.
     */
    exports.pair = function(key, value) {
      let o = {};
      o[key] = value;

      return o;
    };

    /**
     * Converts an input time value from a specified time unit to milliseconds.
     *
     * @param {Number|String} timeValue Input time value.
     * @param {String} timeUnit Time unit string ('minutes', 'hours', 'weeks', 'days', etc.).
     * @returns {Number} Time value in milliseconds.
     */
    exports.millisecondTime = function(timeValue, timeUnit) {
      timeValue = parseInt(timeValue, 10);

      let multi = timeUnitMultipliers[timeUnit];

      if (!multi) throw new Error('Unknown time unit: ' + timeUnit);

      return timeValue * multi.call(timeUnitMultipliers);
    };

    /**
     * Converts time in milliseconds to an input time value from a specified time unit.
     *
     * @param {Number} milliseconds Time in milliseconds.
     * @param {String} timeUnit Time unit string ('minutes', 'hours', 'weeks', 'days', etc.).
     * @returns {Number} Input time value.
     */
    exports.unitTime = function(milliseconds, timeUnit) {
      milliseconds = parseFloat(milliseconds);

      let multi = timeUnitMultipliers[timeUnit];

      if (!multi) throw new Error('Unknown time unit: ' + timeUnit);

      return Math.floor(milliseconds / multi.call(timeUnitMultipliers), 10);
    };

    /**
     * Converts a date or a timestamp to a localized string.
     *
     * @param {Date|Number} date JavaScript Date or a numeric timestamp.
     * @param {String} [locale] Date-time locale. Default is 'ru-RU'.
     * @returns {String} Localized date-time string or default date string representation, if date or
     * locale is invalid.
     */
    exports.localizedDateTime = function(date, locale) {
      if (!date) return '';

      locale = locale || 'ru-RU';

      let _date = (date instanceof Date) ? date : new Date(date);

      if (_.isNaN(_date.getDate())) return date.toString();

      let dateTimeFormat = dateTimeFormats[locale];

      if (!dateTimeFormat) return _date.toLocaleString('ru-RU', defaultDateTimeFormat);

      return dateTimeFormat.format(_date);
    };

    /**
     * Brings a millisecond time value to a closest time unit.
     * In strict mode, brings value to a closest time unit, by which it is divisible.
     * In non-strict mode, brings to an approximate value of a closest time unit.
     *
     * @param {Number} timeValue Millisecond time value.
     * @param {Object} [options] Operation options.
     * - {Boolean} strict Strict mode flag.
     * - {Array} exclude Do not use time units in this array.
     * @returns {Array} Two-element array. First element is a time value in new units; second element is a new unit.
     */
    exports.bringToUnit = function(timeValue, options) {
      options = _.extend({
        strict: true,
        exclude: []
      }, options);
      let multiKeys = Object.keys(timeUnitMultipliers).reverse(); // Go from the greatest multiplier.
      let ret;

      multiKeys.some(function(multiKey) {
        let multiVal = timeUnitMultipliers[multiKey]();
        let units = timeValue / multiVal;

        if (options.exclude.indexOf(multiKey) == -1) {
          if (options.strict && timeValue % multiVal === 0) {
            ret = [units, multiKey];

            return true;
          }
          else if (!options.strict && units >= 1) {
            ret = [Math.round(units), multiKey];

            return true;
          }
        }

        return false;
      });

      if (!ret) {
        if (options.exclude) {
          multiKeys = _.difference(multiKeys, options.exclude);
        }

        ret = [0, _.last(multiKeys)];
      }

      return ret;
    };

    /**
     * Same as standard setInterval() function but with preceding
     * initial synchronous call to the target function.
     *
     * @param {Function} func Target function for setInterval().
     * @param {Number} period Period for setInterval().
     * @returns {Number} Interval handle to be used in clearInterval().
     */
    exports.setIntervalAndGo = function(func, period) {
      func();

      return setInterval(func, period);
    };

    /**
     * Checks if a given value (possibly string) represents a number.
     *
     * @param {*} value Value to test.
     * @returns {Boolean} True is a value represents a number, false otherwise.
     */
    exports.isNumber = function(value) {
      let type = typeof value;

      if (type === 'string') {
        value = value.trim();

        // Discard hex numbers of form "0x123".
        if (value[1] == 'x') return false;
      }

      if (value === '' || value === undefined || value === null) return false;

      if (type === 'boolean' || type === 'object') return false;

      return isFinite(value) && typeof (parseFloat(value)) === 'number';
    };

    /**
     * Checks if the value is empty.
     *
     * @param {*} value Value to check.
     * @returns {Boolean} True if value is empty, false otherwise.
     */
    exports.isEmpty = function(value) {
      return !_.isFinite(value) && !_.isBoolean(value) && _.isEmpty(value);
    };

    /**
     * Escapes string for usage in regular expressions.
     *
     * @param {String} str Input string.
     * @param {Object} [options] Escape options
     * - {Boolean} wildcard Convert asterisk to wildcard selector.
     * @returns {String} Escaped string.
     */
    exports.escapeRegExp = function(str, options) {
      options = options || {};

      str = str.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');

      return options.wildcard ? str.replace(/\\\*/g, '.*') : str;
    };

    /**
     * Tests if a given input is defined.
     *
     * @param {*} input Input to test.
     * @returns {Boolean} True if input is defined, false otherwise.
     */
    exports.isDefined = function(input) {
      return input !== undefined && input !== null;
    };

    /**
     * Checks if a given input is a plain JS object.
     *
     * @param {*} input Input to test.
     * @returns {Boolean} True if an input is plain JS object, false otherwise.
     */
    exports.isPlainObject = function(input) {
      if (typeof input !== 'object') return false;

      if (Object.prototype.toString.call(input) !== '[object Object]') return false;

      if (typeof input.constructor !== 'function') return false;

      let prototype = input.constructor.prototype;

      if (typeof prototype !== 'object') return false;

      if (Object.prototype.toString.call(prototype) !== '[object Object]') return false;

      return prototype.hasOwnProperty('isPrototypeOf');
    };

    /**
     * Try to parse input parameter by JSON.parse, usually string.
     *
     * @param {Any} str Usually string to parse object from.
     * @param {Object} [def] Default value to return if parsing failed.
     * @returns {Object|String} Returns value parsed by JSON.parse or def value.
     * If input string is invalid json string, returns string.
     * If def is set and input string is invalid json string, returns def.
     */
    exports.parseJson = function(str, def) {
      // Inlined _.isObject().
      if (typeof str === 'function' || typeof str === 'object' && !!str) return str;

      try {
        return JSON.parse(str);
      }
      catch (e) {
        return def === undefined ? str : def;
      }
    };

    /**
     * Attempts to parse a boolean from input value.
     *
     * @param {*} value Input value.
     * @returns {Boolean|undefined} Parsed boolean value or undefined, if parsing failed.
     */
    exports.parseBoolean = function(value) {
      if (_.isBoolean(value)) return value;

      if (_.isString(value)) {
        let lowerValue = value.toLowerCase();

        if (lowerValue == 'true') return true;

        if (lowerValue == 'false') return false;

        return undefined;
      }

      if (_.isNumber(value)) {
        return value !== 0;
      }
    };

    /**
     * Analogous to bind(), but binds the specified arguments from the right.
     * Accepts as a first argument a function to bind. Second argument is a
     * context ('this' object). Arguments that follow are concatenated to the
     * arguments, with which the returned function is called.
     *
     * @returns {Function} Function with right-bound arguments.
     */
    exports.bindRight = function() {
      let args0 = _.toArray(arguments);
      let func = args0.shift();
      let context = args0.shift();

      return function() {
        return func.apply(context, _.toArray(arguments).concat(args0));
      };
    };

    /**
     * Breaks an input array into chunks of a given length.
     *
     * @param {Array} arr Input array.
     * @param {Number} chunkLength Chunk length, a positive integer greater than zero.
     * @param {Function} [transformer] Function transforming each element.
     * @returns {Array[]} Array of resulting chunks. The last
     * chunk in this array may be of smaller length.
     */
    exports.chunks = function(arr, chunkLength, transformer) {
      let curChunk = [];
      let result = [];
      let transform = _.isFunction(transformer);

      arr.forEach((elem, idx) => {
        elem = transform ? transformer(elem) : elem;

        if (idx % chunkLength === 0) {
          curChunk = [elem];
          result.push(curChunk);
        }
        else {
          curChunk.push(elem);
        }
      });

      return result;
    };

    /**
     * Parses SAYMON entity ID from string to internal representation.
     *
     * @param {String|ObjectId} id Input ID.
     * @returns {String|Number} Parsed ID.
     */
    exports.parseId = function(id) {
      id = id.toString();

      return id.length !== 24 ? parseInt(id) : id;
    };

    /**
     * Tests if a given string can represent ID of a SAYMON entity.
     *
     * @param {String} str String to check.
     * @returns {Boolean} Test result.
     */
    exports.isSaymonId = function(str) {
      if (exports.isNumber(str)) return true;

      return _.isString(str) && str.length == 24;
    };

    /**
     * Deeply extends target object with source object.
     *
     * @param {Object} target Target object to extend.
     * @param {Object} source Object with new properties.
     * @returns {*|{}} Extended target object.
     */
    exports.deepExtend = function deepExtend(target, source) {
      target = target || {};
      source = source || {};
      let keys = Object.keys(source);

      for (let prop = 0; prop < keys.length; prop++) {
        let value = source[keys[prop]];

        if (_.isObject(value) && !_.isArray(value)) {
          target[keys[prop]] = deepExtend(target[keys[prop]], value);
        }
        else {
          target[keys[prop]] = value;
        }
      }

      return target;
    };

    /**
     * Finds all stat key names from agent data.
     *
     * @param {Object} statPayload Stat payload.
     * @param {Object} options Like this.reduceRecursive().
     * @param {Boolean} options.keysFromFirstArrayRow Return keys without array index prefix. Default true.
     * @returns {String[]} Array of stat names.
     */
    exports.getStatKeyList = function(statPayload, options) {
      options = options || {};

      let keysFromFirstArrayRow = _.isUndefined(options.keysFromFirstArrayRow) || options.keysFromFirstArrayRow;

      let firstEntry = keysFromFirstArrayRow && _.isArray(statPayload) && _.first(statPayload) || statPayload;

      return this.reduceRecursive(firstEntry, (memo, val, key, path) => {
        memo.push(path);

        return memo;
      }, [], _.extend({ skipObjects: false }, options)).sort();
    };

    /**
     * Checks if this stat's payload is a map-like object, that is, an object
     * with object values that have equal key sets.
     *
     * @param {Object} statPayload Stat payload.
     * @returns {Boolean} True if a payload is a map-like object, false otherwise.
     */
    exports.hasMapPayload = function(statPayload) {
      let keys = _.keys(statPayload);

      if (keys.length <= 1) return false;

      let value0 = statPayload[keys[0]];

      if (!_.isObject(value0)) return false;

      let value1 = statPayload[keys[1]];

      if (!_.isObject(value1)) return false;

      return _.first(_.keys(value0)) == _.first(_.keys(value1));
    };

    /**
     * Iterates over array elements in reverse order.
     *
     * @param {Array} arr Array to iterate over.
     * @param {Function} iter Iterator function. Receives value and index as arguments.
     */
    exports.eachReverse = function(arr, iter) {
      if (_.isEmpty(arr)) return;

      for (let i = arr.length - 1; i >= 0; i--) {
        iter(arr[i], i);
      }
    };

    /**
     * Repeats array 'repeat' times.
     *
     * @param {Array} array Input array.
     * @param {Number} repeat Times to repeat.
     * @returns {Array} Repeated array.
     */
    exports.repeatArray = function(array, repeat) {
      return Array.apply(null, { length: repeat * array.length }).map((e, i) => array[i % array.length]);
    };

    /**
     * Wraps an input into an array if it is not yet an array.
     *
     * @param {*} input Input to wrap.
     * @returns {Array|undefined} Wrapped or original array, or undefined or null, if input was undefined or null.
     */
    exports.array = function(input) {
      if (_.isArray(input) || _.isUndefined(input) || _.isNull(input)) return input;

      if (_.isArguments(input)) return _.toArray(input);

      return [input];
    };

    /**
     * Un-wraps input single-element array.
     *
     * @param {*} input Input to un-wrap.
     * @returns {*} First input element, if input is a single-element array,
     * or input as is otherwise.
     */
    exports.unArray = function(input) {
      if (_.isArray(input) && input.length == 1) return input[0];

      return input;
    };

    /**
     * Finds an element insertion index in sorted array.
     *
     * @param {Array} sortedArray Sorted array.
     * @param {*} element Element to insert.
     * @param {Function} comparator Comparator function. Receives 2 elements to compare
     * as an arguments. Returns 0, if elements are equal; positive value, if first element
     * is greater than the second; negative value if first element is less than the second.
     * @returns {Number} Sorted array element insertion index or -1 if an array is empty.
     */
    exports.sortedIndex = function(sortedArray, element, comparator) {
      if (sortedArray.length === 0) return -1;

      let recurse = (array, element, comparator, start, end) => {
        let pivot = (start + end) >> 1; // Math.floor((start + end) / 2).

        let cmpResult = comparator(element, array[pivot]);

        if (end - start <= 1) return cmpResult < 0 ? pivot : pivot + 1; // Here pivot == start and pivot + 1 == end.

        if (cmpResult < 0) {
          return recurse(array, element, comparator, start, pivot);
        }
        else if (cmpResult > 0) {
          return recurse(array, element, comparator, pivot, end);
        }
        else { // Exact match found.
          return pivot;
        }
      };

      return recurse(sortedArray, element, comparator, 0, sortedArray.length);
    };

    /**
     * Performs a safe regular expression matching, treating all regular expression
     * syntax errors as a mismatch.
     *
     * @param {*} string String to match.
     * @param {String} regExp Regular expression.
     * @param {String} [regExpOptions] Regular expression options.
     * @returns {Boolean} Match result.
     */
    exports.match = function(string, regExp, regExpOptions) {
      if (!_.isString(string)) return false;

      try {
        return string.match(new RegExp(regExp, regExpOptions)) !== null;
      }
      catch (e) {
        return false;
      }
    };

    /**
     * Traverses through the json structure and runs callback on its nodes with key-value pair
     * of each object (only with {}, not []) node as parameters.
     *
     * @param {Object|Array} json Object or array to traverse.
     * @param {Function} cb Callback function.
     */
    exports.jsonTreeTraverse = function jsonTreeTraverse(json, cb) {
      if (Array.isArray(json)) {
        json.forEach(obj => jsonTreeTraverse(obj, cb));

        return;
      }
      else if (typeof json != 'object' || json === null) {
        throw new TypeError(`Can not traverse type: ${typeof json}`);
      }

      Object.entries(json).forEach(([key, value]) => {
        cb(key, value);

        try {
          jsonTreeTraverse(value, cb);
        }
        catch (err) { }
      });
    };

    exports.isEmail = function(email) {
      return _.isString(email) && /^[^\s@]+@([^\s@]+\.\w{2,}|\w{2,})$/.test(email);
    };

    /**
     * Parses OpenTsdb duration format to ms.
     * Gets from OpensTsdb code.
     *
     * @param {String} duration Duration in OpenTsdb format.
     * @returns {Number} Duration in OpenTsdb in ms.
     */
    exports.parseDuration = function(duration) {
      let interval = parseInt(duration, 10);

      if (!Number.isNaN(interval)) {
        switch (duration[duration.length - 1]) {
          case 's':
            if (duration.charAt(duration.length - 2) === 'm') {
              return interval;
            }

            return interval * 1000; // seconds
          case 'm':
            return interval * 60 * 1000; // minutes
          case 'h':
            return interval * 3600 * 1000; // hours
          case 'd':
            return interval * 3600 * 24 * 1000; // days
          case 'w':
            return interval * 3600 * 24 * 7 * 1000; // weeks
          case 'n':
            return interval * 3600 * 24 * 30 * 1000; // month (average)
          case 'y':
            return interval * 3600 * 24 * 365 * 1000; // years (screw leap years)
        }
      }

      throw new Error('Bad downsample interval');
    };

    /**
     * Converts any value to string.
     * It performs the same action as the `object.toString()` function
     * but converts scalar values `null` and `undefined` into an empty string.
     *
     * @param {any} value Input value of any type.
     * @returns {String} String value.
     */
    exports.toString = value => exports.isDefined(value) ? value.toString() : '';

    /**
     * Checks arrays equal without order.
     *
     * @param {Array} array1 First array.
     * @param {Array} array2 Second array.
     * @returns {Boolean} Arrays are equal or not.
     */
    exports.arrayEqual = (array1, array2) => {
      if (array1.length != array2.length) {
        return false;
      }

      return _.isEqual(array1.sort(), array2.sort());
    };

    /**
     * Gets type short string type from other type aliases.
     *
     * @param {String|Number} entityType Entity type.
     * @returns {String} String entity type.
     */
    exports.entityTypeToShortString = (entityType) => {
      let types = exports.numericEntityTypes;
  
      switch (entityType) {
        case types.Object:
        case 'obj':
        case 'object':
        case 'objects':
          return 'obj';
  
        case types.Link:
        case 'link':
        case 'links':
          return 'link';
  
        case types.Flow:
        case 'flow':
        case 'flows':
          return types.Flow;
  
        case types.User:
        case 'user':
        case 'users':
          return 'user';
  
        case types.Reference:
        case 'ref':
        case 'refs':
        case 'reference':
        case 'references':
          return 'ref';
  
        default:
          throw new Error(`No short entity name for ${entityType}`);
      }
    };

    return exports;
  };

  if (typeof define !== 'undefined' && define) {
    define(['underscore'], implementation.bind(implementation, {}));
  }
  else {
    implementation(exports, require('underscore'));
  }
}
